import classNames from 'classnames'
import { useCallback, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { getUrlWithParams } from 'core-components/helpers/url'
import { Tooltip } from 'core-components/library/tooltip'

import styles from '../Accordion.module.scss'
import {
  IAccordionGroupProps,
  IAccordionGroupTab,
  IAccordionGroupVerticalViewProps,
} from '../models/IAccordionGroupProps'

interface IVerticalProps extends IAccordionGroupVerticalViewProps {
  className: IAccordionGroupProps['className']
  urlKey: IAccordionGroupProps['urlKey']
  defaultItem: IAccordionGroupProps['defaultItem']
  visibleItems: IAccordionGroupTab[]
}

export const Vertical = ({
  className,
  classes,
  visibleItems,
  sticky,
  urlKey,
  defaultItem,
}: IVerticalProps) => {
  const navigate = useNavigate()
  const isInitCurrentTabSet = useRef<boolean>(false)

  const calcToLink = useCallback(
    (item: IAccordionGroupTab) => {
      if (urlKey) {
        return getUrlWithParams({ [urlKey]: item.id })
      }
      return ''
    },
    [urlKey]
  )

  const scrollIntoView = useCallback((id: string) => {
    const el = document.getElementById(id)
    el?.scrollIntoView({
      block: 'start',
      behavior: 'smooth',
    })
  }, [])

  useEffect(() => {
    if (urlKey && defaultItem && !isInitCurrentTabSet.current) {
      navigate(getUrlWithParams({ [urlKey]: defaultItem }))
      scrollIntoView(defaultItem)
      isInitCurrentTabSet.current = true
    }
  }, [defaultItem, navigate, scrollIntoView, urlKey])

  return (
    <div
      className={classNames(styles.vertical, className || '')}
      data-testid={'accordion_group_vertical_root'}
    >
      <div
        data-testid={'accordion_group_vertical_sidebar'}
        className={classNames(styles.vertical__sidebar, (classes && classes.sidebar) || '')}
      >
        <div
          data-testid={'accordion_group_vertical_tabs'}
          className={classNames(styles.verticalTabs, sticky ? styles['verticalTabs--sticky'] : '')}
        >
          {visibleItems.map((item, index) => {
            const btnProps = {
              className: classNames(
                styles.verticalTabs__item,
                item.icon ? styles['verticalTabs__item--icon'] : ''
              ),
              to: calcToLink(item),
              onClick: () => {
                scrollIntoView(item.id)
              },
              'data-testid': `accordion_group_vertical_item-${item.id}`,
            }

            if (item.icon) {
              return (
                <Tooltip key={index} tipContent={() => <>{item.title && item.title()}</>}>
                  <Link {...btnProps}>{item.icon()}</Link>
                </Tooltip>
              )
            }

            return (
              <Link key={index} {...btnProps}>
                {item.title && item.title()}
              </Link>
            )
          })}
        </div>
      </div>
      <div
        data-testid={'accordion_group_vertical_content'}
        className={classNames(styles.vertical__content, (classes && classes.content) || '')}
      >
        {visibleItems.map((item, index) => {
          return (
            <div key={index} id={item.id} className={styles.verticalCntItem}>
              {item.icon || item.cntTitle || item.title ? (
                <header
                  data-testid={`accordion_group_vertical_header-${item.id}`}
                  className={styles.verticalHeader}
                >
                  {item.icon && item.icon()}
                  {item.cntTitle || item.title ? (
                    <div
                      data-testid={`accordion_group_vertical_header_title-${item.id}`}
                      className={styles.verticalHeader__title}
                    >
                      {item.cntTitle ? item.cntTitle() : item.title && item.title()}
                    </div>
                  ) : null}
                </header>
              ) : null}
              <div key={`${index}-content`}>{item.content()}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
